import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";

/**
 * Combobox — searchable single-select.
 * Mobile: opens as a full-screen bottom sheet (no keyboard issues).
 * Desktop: opens as a positioned dropdown.
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  emptyText = "No matches",
  disabled = false,
  searchPlaceholder = "Search...",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [desktopPlacement, setDesktopPlacement] = useState("bottom");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const updateDesktopPlacement = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || window.innerWidth < 640) return;

    const rect = wrapper.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const dropdownHeight = 300;
    const gap = 8;
    const spaceBelow = viewportHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;

    setDesktopPlacement(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? "top" : "bottom");
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase().replace(/[.\s/-]/g, "");
    const matches = term
      ? options.filter((o) => String(o).toLowerCase().replace(/[.\s/-]/g, "").includes(term))
      : options;

    return [...matches].sort((a, b) => {
      if (a === "Any") return -1;
      if (b === "Any") return 1;
      return 0;
    });
  }, [options, search]);

  // Close on outside click (desktop only)
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Lock body scroll when open on mobile
  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    if (open && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setSearch("");
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Focus search when opened on desktop only
  useEffect(() => {
    if (!open) return undefined;

    updateDesktopPlacement();
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    const timeout = setTimeout(() => {
      if (!isMobile) {
        inputRef.current?.focus();
      }
    }, 100);
    window.addEventListener("resize", updateDesktopPlacement);
    window.addEventListener("scroll", updateDesktopPlacement, true);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", updateDesktopPlacement);
      window.removeEventListener("scroll", updateDesktopPlacement, true);
    };
  }, [open, updateDesktopPlacement]);

  const select = useCallback((option) => {
    if (value === option) {
      onChange?.("");
    } else {
      onChange?.(option);
    }
    setOpen(false);
  }, [onChange, value]);

  return (
    <div ref={wrapperRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(true)}
        className={`flex w-full items-center justify-between gap-2 rounded-2xl border border-sand-300 bg-white px-4 py-3.5 text-left text-[14px] outline-none transition focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 ${
          disabled ? "cursor-not-allowed bg-sand-100 text-ink-400" : "text-ink-800"
        }`}
      >
        <span className={`min-w-0 truncate ${value ? "text-ink-800 font-medium" : "text-ink-300"}`}>
          {value || placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-400 transition hover:bg-sand-200 hover:text-ink-700"
              aria-label="Clear"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown size={16} className={`text-ink-400 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {/* Bottom sheet / dropdown */}
      {open && (
        <>
          {/* Mobile: full bottom sheet */}
          <div className="fixed inset-0 z-[70] flex items-end justify-center sm:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div className="relative z-10 flex max-h-[70vh] w-full flex-col rounded-t-3xl bg-white shadow-elevated">
              <div className="flex shrink-0 items-center gap-3 border-b border-sand-200 px-4 py-3">
                <Search size={16} className="text-ink-400 shrink-0" />
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-[14px] text-ink-800 placeholder:text-ink-300 outline-none"
                  autoComplete="off"
                />
                {search ? (
                  <button type="button" onClick={() => { setSearch(""); inputRef.current?.focus(); }} className="text-ink-400 shrink-0"><X size={15} /></button>
                ) : (
                  <button type="button" onClick={() => setOpen(false)} className="text-[12px] font-semibold text-brand-600 shrink-0">Done</button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain">
                {filtered.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[13px] text-ink-400">{emptyText}</p>
                ) : (
                  <ul role="listbox" className="py-1">
                    {filtered.map((opt) => {
                      const selected = value === opt;
                      return (
                        <li key={opt}>
                          <button type="button" onClick={() => select(opt)} className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[14px] active:bg-sand-100 ${selected ? "bg-brand-50 text-brand-700 font-semibold" : "text-ink-700"}`}>
                            <span className="truncate">{opt}</span>
                            {selected && <Check size={16} className="shrink-0 text-brand-600" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="shrink-0 border-t border-sand-100 px-4 py-2 text-center text-[11px] font-semibold text-ink-400">
                {filtered.length} option{filtered.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Desktop: positioned dropdown */}
          <div
            className={`hidden sm:block absolute left-0 right-0 z-[70] ${
              desktopPlacement === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5"
            }`}
          >
            <div className="overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-elevated">
              <div className="flex items-center gap-2 border-b border-sand-200 px-3 py-2.5">
                <Search size={15} className="text-ink-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-[13.5px] text-ink-800 placeholder:text-ink-300 outline-none"
                  autoComplete="off"
                  autoFocus
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600"><X size={14} /></button>
                )}
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: 240 }}>
                {filtered.length === 0 ? (
                  <p className="px-4 py-5 text-center text-[13px] text-ink-400">{emptyText}</p>
                ) : (
                  <ul role="listbox" className="py-1">
                    {filtered.map((opt) => {
                      const selected = value === opt;
                      return (
                        <li key={opt}>
                          <button type="button" onClick={() => select(opt)} className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13.5px] transition hover:bg-sand-100 ${selected ? "bg-brand-50 text-brand-700 font-semibold" : "text-ink-700"}`}>
                            <span className="truncate">{opt}</span>
                            {selected && <Check size={14} className="shrink-0 text-brand-600" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
